<template>
  <div class="center-content">
    <Card class="w-full">
      <template v-slot:content>
        <h1 class="page-title">Import Fatture Grezze Cei - eDistribuzione</h1>
        <div class="flex align-items-center flex-column">
          <Dropdown
            v-if="directoriesFattureGrezze && directoriesFattureGrezze.length > 0"
            v-model="selectedDirectoryFattureGrezze"
            :options="directoriesFattureGrezze"
            placeholder="Seleziona una directory"
            class="w-full md:w-14rem mb-2"
            :loading="loadingDirectories"
            :disabled="loadingDirectories"
            @change="onDirectoryFattureGrezzeChange"
            optionLabel="label"
            optionValue="value"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
            <template #value="slotProps">
              {{
                slotProps.value
                  ? (directoriesFattureGrezze.find(
                        (d) => d.value === slotProps.value
                      )?.label || slotProps.value)
                  : "Seleziona una directory"
              }}
            </template>
          </Dropdown>
          <div class="button-wrapper">
            <span class="label">
              {{
                fileFattureGrezze
                  ? fileFattureGrezze.name
                  : "Seleziona File"
              }}
            </span>
            <input
              id="uploadFattureGrezze"
              type="file"
              ref="fileInputFattureGrezze"
              @change="handleFileFattureGrezzeUpload($event)"
              accept=".csv, .xlsx, .xls"
            />
          </div>
        </div>
        <div class="text-center">
          <small>Formati consentiti: csv, xlsx, xls</small>
        </div>
        <Button
          @click="uploadFattureGrezze"
          label="Carica File"
          :loading="isUploadingFattureGrezze"
          :disabled="
            !fileFattureGrezze ||
            !selectedDirectoryFattureGrezze ||
            isUploadingFattureGrezze
          "
          class="upload-button"
        />
      </template>
    </Card>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useToast } from "primevue/usetoast";
import Service from "@/services/ask-caricamento-file/caricamento-file";

export default {
  setup() {
    const toast = useToast();
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const directoriesFattureGrezze = ref([]);
    const selectedDirectoryFattureGrezze = ref(null);
    const fileFattureGrezze = ref(null);
    const isUploadingFattureGrezze = ref(false);
    const uploadMessageFattureGrezze = ref("");
    const uploadStatusFattureGrezze = ref("info");
    const loadingDirectories = ref(false);
    const fileInputFattureGrezze = ref(null);

    const loadDirectoriesFattureGrezze = async () => {
      loadingDirectories.value = true;
      try {
        const response = await service.getSubDirectoryFattureGrezzeFornitori();

        if (
          response.status === "success" &&
          response.data &&
          response.data.directories
        ) {
          const filteredDirectories = response.data.directories.filter(
            (dir) =>
              dir.name.toLowerCase().includes("cei") ||
              dir.name.toLowerCase().includes("edistribuzione")
          );

          directoriesFattureGrezze.value = filteredDirectories.map((dir) => {
            const pathParts = dir.name.split("/");
            let displayName = pathParts[0];

            if (
              pathParts.length > 1 &&
              (pathParts[1].toUpperCase() === "EE" ||
                pathParts[1].toUpperCase() === "GAS")
            ) {
              displayName += " - " + pathParts[1].toUpperCase();
            }

            return {
              label: displayName,
              value: dir.name,
            };
          });
        } else {
          console.error("Formato risposta API non valido:", response);
          directoriesFattureGrezze.value = [];
        }
      } catch (error) {
        console.error("Errore durante il caricamento delle directory:", error);
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Errore durante il caricamento delle directory",
        });
        directoriesFattureGrezze.value = [];
      } finally {
        loadingDirectories.value = false;
      }
    };

    const onDirectoryFattureGrezzeChange = () => {
      fileFattureGrezze.value = null;
      uploadMessageFattureGrezze.value = "";
    };

    const uploadFattureGrezze = async () => {
      if (!fileFattureGrezze.value || !selectedDirectoryFattureGrezze.value) {
        uploadMessageFattureGrezze.value = "Seleziona un file e una directory";
        uploadStatusFattureGrezze.value = "warning";
        return;
      }

      isUploadingFattureGrezze.value = true;
      uploadMessageFattureGrezze.value = "Caricamento in corso...";
      uploadStatusFattureGrezze.value = "info";

      try {
        const formData = new FormData();
        formData.append("file", fileFattureGrezze.value);

        const cleanPath = selectedDirectoryFattureGrezze.value.replace(
          /^\/+|\/+$/g,
          ""
        );
        formData.append("directory_path", cleanPath);

        const result = await service.importFattureGrezzeFornitori(formData);

        if (result.status === "success") {
          uploadMessageFattureGrezze.value =
            result.message || "File caricato con successo!";
          uploadStatusFattureGrezze.value = "success";
          fileFattureGrezze.value = null;

          if (fileInputFattureGrezze.value) {
            fileInputFattureGrezze.value.value = "";
          }

          toast.add({
            severity: "success",
            summary: "Successo",
            detail: result.message || "File caricato con successo",
          });
        } else {
          throw new Error(
            result.message || "Errore durante il caricamento del file"
          );
        }
      } catch (error) {
        console.error("Errore durante il caricamento del file:", error);

        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Errore durante il caricamento del file";
        uploadMessageFattureGrezze.value = errorMessage;
        uploadStatusFattureGrezze.value = "error";

        toast.add({
          severity: "error",
          summary: "Errore",
          detail: errorMessage,
        });
      } finally {
        isUploadingFattureGrezze.value = false;
      }
    };

    const handleFileFattureGrezzeUpload = (evt) => {
      evt.preventDefault();
      const file = evt.target.files[0];
      if (file) {
        const extension = file.name.split(".").pop().toLowerCase();
        if (["csv", "xlsx", "xls"].includes(extension)) {
          fileFattureGrezze.value = file;
        } else {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail:
              "Formato file non valido. Sono consentiti solo file CSV, XLSX e XLS",
          });
          evt.target.value = "";
          fileFattureGrezze.value = null;
        }
      } else {
        fileFattureGrezze.value = null;
      }
    };

    onMounted(async () => {
      await loadDirectoriesFattureGrezze();
    });

    return {
      directoriesFattureGrezze,
      selectedDirectoryFattureGrezze,
      fileFattureGrezze,
      isUploadingFattureGrezze,
      uploadMessageFattureGrezze,
      uploadStatusFattureGrezze,
      loadingDirectories,
      fileInputFattureGrezze,
      loadDirectoriesFattureGrezze,
      onDirectoryFattureGrezzeChange,
      uploadFattureGrezze,
      handleFileFattureGrezzeUpload,
    };
  },
};
</script>

<style scoped>
.center-content {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.page-title {
  font-size: 20px;
  text-align: center;
}

.button-wrapper {
  position: relative;
  width: 150px;
  text-align: center;
  margin-top: 1rem;
}

.button-wrapper span.label {
  position: relative;
  z-index: 0;
  display: inline-block;
  width: 100%;
  background: #00bfff;
  cursor: pointer;
  color: #544848;
  padding: 10px 0;
  text-transform: uppercase;
  font-size: 12px;
  border-radius: 3px;
  -webkit-box-shadow: 0px 10px 13px -7px #00000050,
    5px 5px 15px 5px rgba(0, 0, 0, 0);
  box-shadow: 0px 10px 13px -7px #00000050,
    5px 5px 15px 5px rgba(0, 0, 0, 0);
}

#uploadFattureGrezze {
  display: inline-block;
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 50px;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
}

.upload-button {
  margin-top: 1.5rem;
}
</style>

